import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { createChatApi, requestWebNotificationPermission } from '../services/staffChat.js'

const POLL_MS = 3000
const TYPING_PING_MS = 3000

function timeLabel(str) {
  const d = new Date(str)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  }
  const diffDays = (now - d) / 86_400_000
  if (diffDays < 7) return d.toLocaleDateString(undefined, { weekday: 'short' })
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

function dayLabel(str) {
  const d = new Date(str)
  const today = new Date()
  const yesterday = new Date(Date.now() - 86_400_000)
  if (d.toDateString() === today.toDateString()) return 'Today'
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })
}

function initials(name) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?'
}

function Avatar({ name, team, online }) {
  return (
    <span className={`chat-avatar ${team ? 'team' : ''}`}>
      {team ? '✝' : initials(name)}
      {online && <span className="chat-online-dot" aria-label="Active now" />}
    </span>
  )
}

/**
 * Interner Chat für Admin und Seelsorger.
 * Welche Unterhaltung offen ist bzw. ob "Neue Nachricht" angezeigt wird, steuert die Seite
 * über die URL (eigener Verlaufseintrag je Schritt → Zurück geht genau einen Schritt).
 *  getToken  – liefert das Bearer-Token des eingeloggten Teammitglieds
 *  activeId  – offene Unterhaltung oder null
 *  showPicker – "Neue Nachricht"-Auswahl anzeigen
 *  onOpen(id) / onPicker() / onStart(id) / onBack() – Navigation
 */
export default function StaffChat({ getToken, activeId, showPicker, onOpen, onPicker, onStart, onBack, onActivity }) {
  const api = useMemo(() => createChatApi(getToken), [getToken])
  const [conversations, setConversations] = useState(null)
  const [people, setPeople] = useState(null) // Personen für "Neue Nachricht"
  const [error, setError] = useState(null)

  const loadConversations = useCallback(async () => {
    try {
      setConversations(await api('/conversations'))
      setError(null)
    } catch (err) {
      setError(err.message)
    }
  }, [api])

  useEffect(() => {
    requestWebNotificationPermission()
    loadConversations()
    const timer = setInterval(loadConversations, activeId ? POLL_MS * 3 : POLL_MS * 2)
    return () => clearInterval(timer)
  }, [loadConversations, activeId])

  useEffect(() => {
    if (!showPicker) return
    setPeople(null)
    api('/directory').then(d => setPeople(d.people)).catch(err => setError(err.message))
  }, [api, showPicker])

  async function startDirect(staffKey) {
    try {
      const { id } = await api('/conversations/direct', { method: 'POST', body: JSON.stringify({ staff_key: staffKey }) })
      onStart(id)
      loadConversations()
    } catch (err) {
      setError(err.message)
    }
  }

  const active = conversations?.find(c => c.id === activeId) || null

  return (
    <div className={`chat ${activeId ? 'has-active' : ''}`}>
      <aside className="chat-sidebar">
        <div className="chat-sidebar-header">
          <h2>Chats</h2>
          <button className="btn btn-ghost" onClick={onPicker}>✎ New</button>
        </div>
        {error && <p className="comment-error chat-error">{error}</p>}

        {showPicker ? (
          <div className="chat-picker">
            <div className="chat-picker-header">
              <span>New message to…</span>
              <button className="action-btn" onClick={onBack}>Cancel</button>
            </div>
            {people === null && <div className="spinner" aria-label="Loading…" />}
            {people?.length === 0 && <p className="comment-empty">No other team members yet.</p>}
            {people?.map(p => (
              <button key={p.key} className="chat-row" onClick={() => startDirect(p.key)}>
                <Avatar name={p.name} online={p.online} />
                <span className="chat-row-main">
                  <span className="chat-row-title">{p.name}</span>
                  <span className="chat-row-sub">{p.online ? 'Active now' : 'Offline'}</span>
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="chat-list">
            {conversations === null && <div className="spinner" aria-label="Loading…" />}
            {conversations?.map(c => (
              <button
                key={c.id}
                className={`chat-row ${c.id === activeId ? 'active' : ''} ${c.unread ? 'unread' : ''}`}
                onClick={() => onOpen(c.id)}
              >
                <Avatar name={c.title} team={c.kind === 'team'} online={c.online} />
                <span className="chat-row-main">
                  <span className="chat-row-title">{c.title}</span>
                  <span className="chat-row-sub">
                    {c.last_message
                      ? `${c.last_message.mine ? 'You: ' : c.kind === 'team' ? `${c.last_message.sender_name.split(' ')[0]}: ` : ''}${c.last_message.text}`
                      : c.kind === 'team' ? 'Group chat for all pastors & admin' : 'Say hello 👋'}
                  </span>
                </span>
                <span className="chat-row-side">
                  {c.last_message && <time>{timeLabel(c.last_message.created_at)}</time>}
                  {c.unread > 0 && <span className="chat-unread">{c.unread}</span>}
                </span>
              </button>
            ))}
          </div>
        )}
      </aside>

      <section className="chat-main">
        {active ? (
          <Thread
            key={active.id}
            api={api}
            conversation={active}
            onBack={onBack}
            onActivity={() => { loadConversations(); onActivity?.() }}
          />
        ) : (
          <div className="chat-empty">
            <span>💬</span>
            <p>Select a chat or start a new one.</p>
          </div>
        )}
      </section>
    </div>
  )
}

function Thread({ api, conversation: c, onBack, onActivity }) {
  const [messages, setMessages] = useState([])
  const [members, setMembers] = useState([])
  const [typing, setTyping] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const listRef = useRef(null)
  const lastIdRef = useRef(0)
  const stickToBottom = useRef(true)
  const lastTypingPing = useRef(0)
  // Stabile Referenz, damit das Polling nicht bei jedem Render neu startet
  const activityRef = useRef(onActivity)
  activityRef.current = onActivity

  const merge = useCallback((incoming) => {
    if (!incoming.length) return
    setMessages(prev => {
      const seen = new Set(prev.map(m => m.id))
      return [...prev, ...incoming.filter(m => !seen.has(m.id))]
    })
    lastIdRef.current = Math.max(lastIdRef.current, ...incoming.map(m => m.id))
  }, [])

  const poll = useCallback(async (initial = false) => {
    try {
      const read = document.visibilityState === 'visible' ? '&read=1' : ''
      const after = initial ? '' : `after=${lastIdRef.current}`
      const data = await api(`/conversations/${c.id}/messages?${after}${read}`)
      if (initial) setHasMore(data.has_more)
      const hadNew = data.messages.some(m => m.id > lastIdRef.current)
      merge(data.messages)
      setMembers(data.members)
      setTyping(data.typing)
      setLoaded(true)
      if (initial || (hadNew && read)) activityRef.current()
    } catch (err) {
      setError(err.message)
    }
  }, [api, c.id, merge])

  useEffect(() => {
    poll(true)
    const timer = setInterval(() => poll(false), POLL_MS)
    return () => clearInterval(timer)
  }, [poll])

  // Automatisch nach unten scrollen, solange man unten ist
  useEffect(() => {
    const el = listRef.current
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight
  }, [messages, typing])

  function onScroll() {
    const el = listRef.current
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  async function loadOlder() {
    const el = listRef.current
    const prevHeight = el.scrollHeight
    try {
      const data = await api(`/conversations/${c.id}/messages?before=${messages[0].id}`)
      setHasMore(data.has_more)
      setMessages(prev => [...data.messages, ...prev])
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight - prevHeight })
    } catch (err) {
      setError(err.message)
    }
  }

  function onChange(e) {
    setText(e.target.value)
    if (Date.now() - lastTypingPing.current > TYPING_PING_MS && e.target.value.trim()) {
      lastTypingPing.current = Date.now()
      api(`/conversations/${c.id}/typing`, { method: 'POST', body: '{}' }).catch(() => {})
    }
  }

  async function send(e) {
    e?.preventDefault()
    const body = text.trim()
    if (!body || sending) return
    setSending(true)
    setError(null)
    try {
      const msg = await api(`/conversations/${c.id}/messages`, { method: 'POST', body: JSON.stringify({ text: body }) })
      stickToBottom.current = true
      merge([msg])
      setText('')
      lastTypingPing.current = 0
      activityRef.current()
    } catch (err) {
      setError(err.message)
    } finally {
      setSending(false)
    }
  }

  function onKeyDown(e) {
    // Enter sendet, Shift+Enter macht eine neue Zeile (wie im Messenger)
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      send()
    }
  }

  // "Seen" unter der eigenen letzten Nachricht
  const lastMine = [...messages].reverse().find(m => m.mine)
  const seenBy = lastMine ? members.filter(m => m.last_read_message_id >= lastMine.id).map(m => m.name) : []
  const team = c.kind === 'team'
  const other = !team ? members[0] : null

  return (
    <>
      <header className="chat-thread-header">
        <button className="chat-back" onClick={onBack} aria-label="Back to chats">←</button>
        <Avatar name={c.title} team={team} online={!team && other?.online} />
        <div>
          <strong>{c.title}</strong>
          <span className="chat-row-sub">
            {team
              ? `${members.length + 1} members`
              : !c.other_active ? 'Account deactivated' : other?.online ? 'Active now' : 'Offline'}
          </span>
        </div>
      </header>

      <div className="chat-messages" ref={listRef} onScroll={onScroll}>
        {hasMore && <button className="action-btn chat-older" onClick={loadOlder}>Load earlier messages</button>}
        {loaded && messages.length === 0 && <p className="chat-empty-thread">No messages yet. Say hello! 👋</p>}
        {messages.map((m, i) => {
          const prev = messages[i - 1]
          const newDay = !prev || new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString()
          const grouped = prev && !newDay && prev.mine === m.mine && prev.sender_name === m.sender_name
            && new Date(m.created_at) - new Date(prev.created_at) < 5 * 60_000
          return (
            <div key={m.id}>
              {newDay && <div className="chat-day">{dayLabel(m.created_at)}</div>}
              <div className={`chat-bubble-row ${m.mine ? 'mine' : ''} ${grouped ? 'grouped' : ''}`}>
                {team && !m.mine && !grouped && <span className="chat-sender">{m.sender_name}</span>}
                <div className="chat-bubble" title={new Date(m.created_at).toLocaleString()}>
                  {m.text}
                </div>
              </div>
              {m === lastMine && seenBy.length > 0 && (
                <div className="chat-seen">{team ? `Seen by ${seenBy.join(', ')}` : 'Seen'}</div>
              )}
            </div>
          )
        })}
        {typing.length > 0 && (
          <div className="chat-typing">
            <span className="chat-typing-dots"><i /><i /><i /></span>
            {team ? `${typing.join(', ')} ${typing.length === 1 ? 'is' : 'are'} typing…` : 'typing…'}
          </div>
        )}
      </div>

      {error && <p className="comment-error chat-error">{error}</p>}

      <form className="chat-composer" onSubmit={send}>
        <textarea
          className="comment-input"
          rows={1}
          maxLength={2000}
          placeholder={c.other_active ? 'Aa' : 'This account has been deactivated'}
          value={text}
          onChange={onChange}
          onKeyDown={onKeyDown}
          disabled={!c.other_active}
        />
        <button className="btn btn-primary" type="submit" disabled={sending || !text.trim() || !c.other_active}>
          Send
        </button>
      </form>
    </>
  )
}
